import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  useWatch,
  useFieldArray,
  Controller,
  useController,
} from "react-hook-form";
import {
  FormControl,
  InputLabel,
  Box,
  Button,
  TextField,
  List,
  Paper,
  ListSubheader,
  ListItem,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { selectAllOrgDevicesWithFacilityName } from "../../../../../state/slices/OrgTree";
import clsx from "clsx";
import { useStyles } from "./DeviceSelectionFormField.styles";

export default function DeviceSelectionFormField({ control, errors, trigger }) {
  const devices = useSelector((state) =>
    selectAllOrgDevicesWithFacilityName(state)
  );
  const classes = useStyles();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "deviceIds",
    shouldUnregister: true,
  });

  const [selectedToRemove, setSelectedToRemove] = useState([]);
  const [autoCompleteInputValue, setAutoCompleteInputValue] = useState(null); //

  const handleAddDevice = (_, selectedDevice) => {
    if (
      selectedDevice &&
      !fields.find((field) => field.deviceId === selectedDevice.deviceId)
    ) {
      append({ deviceId: selectedDevice.deviceId }); // Store as an object { deviceId: "GUID" }
      trigger("deviceIds"); //Needed to rerun validation when using useFieldArray
    }
    setAutoCompleteInputValue("");
  };

  const handleRemoveSelected = () => {
    const updatedDeviceIds = fields.filter(
      (device) => !selectedToRemove.includes(device.deviceId)
    );

    replace(updatedDeviceIds);
    setSelectedToRemove([]);
    trigger("deviceIds");
  };

  const handleSelectOrDeselectAll = () => {
    if (selectedToRemove.length === 0) {
      const allCurrentDeviceIds = fields.map(field => field.deviceId);
      setSelectedToRemove(allCurrentDeviceIds);
    } else {
      setSelectedToRemove([]);
    }
  };


  return (
    <Box display="flex" flexDirection="column">
      {/* Device Selection Autocomplete */}
      <Autocomplete
        className={clsx([classes.autoComplete])}
        value={null}
        options={devices.filter(
          (d) => !fields.some((f) => f.deviceId == d.deviceId)
        ).toSorted((a, b) =>
          a.facilityName.localeCompare(b.facilityName) || a.deviceName.localeCompare(b.deviceName)
        )}
        groupBy={(option) => option.facilityName}
        getOptionLabel={(option) =>
          option ? `${option.deviceName} (${option.facilityName})` : ""
        }
        onChange={handleAddDevice}
        inputValue={autoCompleteInputValue}
        onInputChange={(_, newValue) =>
          setAutoCompleteInputValue(newValue || "")
        }
        clearOnBlur
        renderInput={(params) => (
          <TextField
            {...params}
            label="Available Devices"
            variant="standard"
            fullWidth
            error={!!errors.deviceIds}
            helperText={errors.deviceIds?.message}
          />
        )}
      />

      <FormControl fullWidth>
        <InputLabel shrink={true}>Selected Devices</InputLabel>
        <Controller
          name="deviceIds"
          control={control}
          render={({ field }) => (
            <Paper
              elevation={1}
              style={{
                maxHeight: 300,
                overflow: "auto",
                marginTop: 17,
                border: "1px solid #e0e0e0",
                borderRadius: 4,
              }}
            >
              <List dense>
                {(() => {
                  if (fields.length === 0){
                    return (
                      <ListItem
                        key={"no-device-notification"}
                        className={clsx([classes.noDeviceItem])}
                        dense
                      >
                        No Device Selected
                      </ListItem>
                    )
                  }
                  // Group devices by facility
                  const facilitiesMap = {};

                  fields.forEach((device) => {
                    const deviceInfo = devices.find(
                      (d) => d.deviceId === device.deviceId
                    );
                    if (deviceInfo) {
                      const { facilityName } = deviceInfo;
                      if (!facilitiesMap[facilityName]) {
                        facilitiesMap[facilityName] = [];
                      }
                      facilitiesMap[facilityName].push({
                        ...device,
                        ...deviceInfo,
                      });
                    } else {
                      // Handle devices without facility info
                      if (!facilitiesMap["Unknown"]) {
                        facilitiesMap["Unknown"] = [];
                      }
                      facilitiesMap["Unknown"].push(device);
                    }
                  });

                  // Sort facilities alphabetically
                  const sortedFacilities = Object.keys(facilitiesMap).sort();

                  return sortedFacilities.flatMap(
                    (facilityName, facilityIndex) => {
                      const facilityDevices = facilitiesMap[facilityName];

                      return [
                        // Facility header
                        <ListSubheader
                          key={`facility-${facilityName}`}
                          component="div"
                          className={clsx([classes.deviceSubheader])}
                          disableSticky
                          style={{
                            borderTop:
                              facilityIndex > 0 ? "1px solid #e0e0e0" : "none",
                          }}
                        >
                          {facilityName}
                        </ListSubheader>,

                        // Devices in this facility
                        ...facilityDevices.map((device) => {
                          const isChecked = selectedToRemove.includes(
                            device.deviceId
                          );

                          const toggleCheckbox = () => {
                            if (isChecked) {
                              setSelectedToRemove(
                                selectedToRemove.filter(
                                  (id) => id !== device.deviceId
                                )
                              );
                            } else {
                              setSelectedToRemove([
                                ...selectedToRemove,
                                device.deviceId,
                              ]);
                            }
                          };

                          return (
                            <ListItem
                              key={device.deviceId}
                              dense
                              button
                              onClick={toggleCheckbox}
                            >
                              <Checkbox
                                edge="start"
                                checked={isChecked}
                                onChange={toggleCheckbox}
                                tabIndex={-1}
                                disableRipple
                                color="primary"
                              />
                              <ListItemText
                                primary={device.deviceName || device.deviceId}
                              />
                            </ListItem>
                          );
                        }),
                      ];
                    }
                  );
                })()}
              </List>
            </Paper>
          )}
        />

        <Box mt={2} display="flex" justifyContent="space-between">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSelectOrDeselectAll}
            disabled={fields.length === 0}
          >
            {
              selectedToRemove.length === 0 ? "Select All" : "Deselect All"
            }
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRemoveSelected}
            disabled={selectedToRemove.length === 0}
          >
            Remove Selected Devices
          </Button>
        </Box>
      </FormControl>
    </Box>
  );
}
